import { Avatar, AvatarFallback } from '@ophelos/ui/components/avatar';
import { useEffect, useState } from 'react';
import { useTheme } from '@/components/layout/ThemeProvider';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { getCreditorInitials } from '@/lib/creditor';

interface CreditorLogoProps {
  name: string;
  logoUrl?: string;
  className?: string;
}

/**
 * The creditor's mark, so a payment screen is recognisably theirs. Tries the client's own logo,
 * then the portal's branding icon (what the dashboard header shows), and settles on initials
 * when neither loads — a dead URL must never leave a broken image in the header.
 */
export function CreditorLogo({ name, logoUrl, className = '' }: CreditorLogoProps) {
  const { theme } = useTheme();
  const { config } = useConfiguration();
  const brandingIcon = config?.ui?.branding?.logoIcon;
  const brandingIconUrl = theme === 'dark' ? brandingIcon?.dark : brandingIcon?.light;

  const candidates = [logoUrl, brandingIconUrl].filter((url): url is string => !!url);
  const [failed, setFailed] = useState<string[]>([]);
  const src = candidates.find((url) => !failed.includes(url));

  // A new case or theme brings new candidates; give them a fresh attempt.
  useEffect(() => setFailed([]), [logoUrl, brandingIconUrl]);

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        title={name}
        className={`h-8 max-w-24 w-auto object-contain ${className}`}
        onError={() => setFailed((prev) => [...prev, src])}
      />
    );
  }
  return (
    <Avatar className={`size-9 ${className}`} title={name}>
      <AvatarFallback className="text-sm font-semibold bg-accent text-accent-foreground">{getCreditorInitials(name)}</AvatarFallback>
    </Avatar>
  );
}
